import React, { ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TextGradient } from "../Gradient";
import { Button } from "../Buttons";
import { getGlassCardStyles } from "../../utility/glassCard.utils";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  icon?: ReactNode;
  iconColor?: string;
  actions?: ReactNode;
  cancelText?: string;
  confirmText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmButtonVariant?: "contained" | "outlined" | "text" | "elevated" | "icon";
  confirmButtonColor?: "default" | "error" | "primary" | "secondary";
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  fullWidth?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  description,
  icon,
  iconColor,
  actions,
  cancelText = "Cancel",
  confirmText = "Confirm",
  onConfirm,
  onCancel,
  confirmButtonVariant = "contained",
  confirmButtonColor = "default",
  maxWidth = "xs",
  fullWidth = true,
}) => {
  const theme = useTheme();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const getConfirmButtonStyles = () => {
    if (confirmButtonColor === "error") {
      return {
        backgroundColor: theme.palette.error.main,
        backgroundImage: "linear-gradient(135deg, #d32f2f 0%, #f44336 100%)",
        color: "#fff",
        "&:hover": {
          backgroundColor: theme.palette.error.dark,
          backgroundImage: "linear-gradient(135deg, #c62828 0%, #e53935 100%)",
        },
      };
    }
    return {};
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      TransitionProps={{
        timeout: 150, // Fast transition (reduced from default 225ms)
      }}
      PaperProps={{
        elevation: 0,
        sx: (theme) => ({
          borderRadius: 3,
          p: 0,
          ...getGlassCardStyles(theme),
        }),
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={handleCancel}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "text.secondary",
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Header */}
      <DialogTitle
        sx={{
          textAlign: "center",
          pt: { xs: 3, sm: 4 },
          pb: description ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {icon && (
          <Box
            sx={{
              mb: { xs: 1.5, sm: 2 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              "& svg": {
                fontSize: { xs: 40, sm: 48 },
                color: iconColor || theme.palette.primary.main,
                display: "block",
                flexShrink: 0,
              },
            }}
          >
            {icon}
          </Box>
        )}
        <TextGradient
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.125rem", sm: "1.25rem" },
            wordBreak: "break-word",
          }}
        >
          {title}
        </TextGradient>
      </DialogTitle>

      {/* Description */}
      {description && (
        <DialogContent 
          sx={{ 
            textAlign: "center", 
            pb: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
            pt: 0,
          }}
        >
          <TextGradient
            variant="body1"
            sx={{
              color: "text.secondary",
              lineHeight: 1.6,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              wordBreak: "break-word",
            }}
          >
            {description}
          </TextGradient>
        </DialogContent>
      )}

      {/* Actions */}
      <DialogActions 
        sx={{ 
          justifyContent: "center", 
          gap: { xs: 1.5, sm: 2 },
          pb: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          flexWrap: "wrap",
        }}
      >
        {actions ? (
          actions
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                minWidth: 120,
              }}
            >
              {cancelText}
            </Button>
            {onConfirm && (
              <Button
                variant={confirmButtonVariant}
                onClick={handleConfirm}
                sx={{
                  minWidth: 140,
                  ...getConfirmButtonStyles(),
                }}
              >
                {confirmText}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default Modal;

